import { useState, useEffect, useRef } from 'react';
import { processText, svgToDataUrl, parseArrowKey } from '../../../utils/learnUtils';
import { useLearnCtx } from '.././LearnPage';
import { useMathJax } from '../../../hooks/useMathJax';
import { useCaptionHighlight } from '../../../hooks/useCaptionHighlight';
import SelectionToolbar from './SelectionToolbar';
import MultiFigureViewer from './MultiFigureViewer';

/**
 * Renders EDCOMP_MULTIFIGURE.
 *
 * Two modes are selected by data.multitype:
 *   'manual'   → MultiFigureViewer (inline, prev/next arrows, no scroll pause)
 *   anything else → MultiFigureScroll (sticky scroll-through, see below)
 *
 * The "on-pause-off" scroll type is also shown via MultiFigureViewer when the
 * expand button is clicked (full lightbox) or inside the SupportPanel.
 *
 * @param {{ item: object }} props
 */
export default function MultiFigureComp({ item }) {
  const { data } = item;
  const { setLightbox } = useLearnCtx();
  const images   = (data?.image ?? '').split(';;;split;;;');
  const captions = (data?.text  ?? '').split(';;;split;;;');

  if (data?.multitype === 'manual' || data?.multitype === 'background-manual') {
    const isBackground = data?.multitype === 'background-manual';
    const viewImages   = isBackground ? images.slice(1)   : images;
    const viewCaptions = isBackground ? captions.slice(1) : captions;
    const bgImage      = isBackground ? images[0]         : undefined;
    const figureWidth  = data?.width ?? '100%';

    const handleExpand = (currentIdx) => {
      setLightbox({
        images: viewImages,
        captions: viewCaptions,
        initialIdx: currentIdx,
        expandedWidth: data?.expanded_width,
        ...(isBackground && { backgroundImage: bgImage }),
      });
    };
    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: figureWidth }}>
          <MultiFigureViewer
            images={viewImages}
            captions={viewCaptions}
            backgroundImage={bgImage}
            onExpand={handleExpand}
            showInstructions
            outerNav={figureWidth === '100%'}
            itemId={item._id}
          />
        </div>
      </div>
    );
  }

  const isBackgroundFade = data?.multitype === 'background-fade';
  const scrollImages   = isBackgroundFade ? images.slice(1)   : images;
  const scrollCaptions = isBackgroundFade ? captions.slice(1) : captions;
  const scrollBgImage  = isBackgroundFade ? images[0]         : undefined;

  return <MultiFigureScroll item={item} images={scrollImages} captions={scrollCaptions} backgroundImage={scrollBgImage} />;
}

/* ─────────────────────────────────────────────────────────────────────────
 * Scroll-through mode (on-pause-off)
 *
 * Scroll geometry  (canvas H ≈ 100dvh, contentH = measured figure+progress px):
 *   T              = panelH/2 − contentH/2   (sticky top, pixels)
 *   sticky top     = T  →  element sticks when its centre hits 50dvh
 *   sticky height  = contentH (auto)  →  sticky bottom = progress-bar bottom
 *   sticky phase   = wrapper_height − contentH = n_state × 100dvh
 *   wrapper height = n_state × 100dvh + contentH
 *
 *   raw = max(0, (scrolledInto + T) / panelH)
 *     → raw = 0 when figure first sticks
 *     → raw = n_state when sticky releases (last frame complete)
 *
 * Timeline:
 *   Approach     — figure rises naturally, no gap above, raw = 0 (frame 0)
 *   raw 0 → 1   — frame 0; content-above scrolls off during the first T px
 *   raw 1 → n   — frames 1 … n-1; last frame completes at sticky release
 *   Release      — sticky bottom = content-below leading edge (progress-bar bottom)
 * ───────────────────────────────────────────────────────────────────────── */
function MultiFigureScroll({ item, images, captions, backgroundImage }) {
  const { setLightbox } = useLearnCtx();
  const { n_state } = item;

  const [panelIdx,  setPanelIdx]  = useState(0);
  const [progress,  setProgress]  = useState(0);
  // Measured pixel height of .ct__mfig-content (figure + gap + progress bar).
  const [contentH,  setContentH]  = useState(0);
  const [activeKeys, setActiveKeys] = useState(new Set());
  const wrapperRef = useRef(null);
  const captionRef = useRef(null);       // wrapper div — MathJax + pointer buttons
  const activeCaptionRef = useRef(null); // current figcaption — highlight tracking
  const contentRef = useRef(null);

  // Keep activeCaptionRef in sync with the visible panel
  useEffect(() => {
    const figcaptions = captionRef.current?.querySelectorAll('figcaption');
    activeCaptionRef.current = figcaptions?.[panelIdx] ?? null;
  }, [panelIdx]);

  const capHL = useCaptionHighlight(
    activeCaptionRef,
    `${item._id}-cap-${panelIdx}`,
  );

  useMathJax(captionRef, [panelIdx], capHL.onTypesetRef);

  // Clear arrows when the visible panel changes
  useEffect(() => { setActiveKeys(new Set()); }, [panelIdx]);

  useEffect(() => {
    const el = captionRef.current;
    if (!el) return;
    const onClick = (e) => {
      const btn = e.target.closest('.ct__fig-ptr-btn');
      if (!btn) return;
      e.preventDefault();
      const key = btn.dataset.arrows;
      setActiveKeys(prev => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
      });
    };
    el.addEventListener('click', onClick);
    return () => el.removeEventListener('click', onClick);
  }, []);

  useEffect(() => {
    const el = captionRef.current;
    if (!el) return;
    el.querySelectorAll('.ct__fig-ptr-btn').forEach(btn => {
      btn.classList.toggle('ct__fig-ptr-btn--active', activeKeys.has(btn.dataset.arrows));
    });
  }, [activeKeys]);

  const visibleArrows = [...activeKeys].flatMap(parseArrowKey);

  /* Measure the inner content block whenever it resizes */
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setContentH(entry.contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /* Scroll handler — re-bound when n_state or contentH changes */
  useEffect(() => {
    const canvas = wrapperRef.current?.closest('.learn__canvas-panel');
    if (!canvas || !wrapperRef.current) return;

    const onScroll = () => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      const canvasRect  = canvas.getBoundingClientRect();
      const wrapperRect = wrapper.getBoundingClientRect();
      const scrolledInto = canvasRect.top - wrapperRect.top;
      const panelH = canvas.clientHeight;
      if (panelH <= 0) return;

      const T   = panelH / 2 - contentH / 2;
      const raw = Math.max(0, (scrolledInto + T) / panelH);
      const idx  = Math.min(n_state - 1, Math.floor(raw));
      const prog = raw >= n_state ? 1 : raw - Math.floor(raw);
      setPanelIdx(idx);
      setProgress(prog);
    };

    canvas.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => canvas.removeEventListener('scroll', onScroll);
  }, [n_state, contentH]);

  // Open the lightbox as a navigable multi-figure viewer.
  const handleExpand = () => {
    setLightbox({ images, captions, initialIdx: panelIdx, expandedWidth: item.data?.expanded_width });
  };

  const figureWidth = item.data?.width ?? '100%';
  const halfH = (contentH / 2).toFixed(1);
  const stickyT  = `calc(50dvh - ${halfH}px)`;
  const wrapperH = `calc(${Math.max(1, n_state)} * 100dvh + ${contentH.toFixed(1)}px)`;

  return (
    <div ref={wrapperRef} className="ct__mfig-wrapper" style={{ height: wrapperH }}>
      <div className="ct__mfig-sticky" style={{ top: stickyT }}>
        <div ref={contentRef} className="ct__mfig-content" style={{ width: figureWidth }}>

          {/* Figure: image + caption */}
          <div className="ct__mfig-figure">
            <div className="ct__mfig-img-area">
              {backgroundImage && (
                <img
                  src={svgToDataUrl(backgroundImage)}
                  alt="Background"
                  className="ct__mfig-img ct__mfig-img--background"
                />
              )}
              {images.map((svg, i) => (
                svg ? (
                  <img
                    key={i}
                    src={svgToDataUrl(svg)}
                    alt={`Panel ${i + 1}`}
                    className={`ct__mfig-img ${i === panelIdx ? 'ct__mfig-img--active' : ''}`}
                  />
                ) : null
              ))}
              {visibleArrows.map(({ x, y }, i) => <PointerArrow key={i} x={x} y={y} />)}
            </div>
            <button
              className="ct__figure-expand"
              onClick={handleExpand}
              aria-label="Expand figure"
              title="Expand figure"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M2 2h4M2 2v4M12 2H8M12 2v4M2 12h4M2 12V8M12 12H8M12 12V8"
                      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>

            {/* All captions grid-stacked so height never shifts between panels */}
            <div ref={captionRef} className="ct__mfig-captions">
              {captions.map((caption, i) => (
                <figcaption
                  key={i}
                  className={`ct__mfig-caption${i === panelIdx ? ' ct__mfig-caption--active' : ''}`}
                  dangerouslySetInnerHTML={{ __html: processText(caption) }}
                />
              ))}
            </div>
          </div>

          {/* Segmented scroll-progress bar */}
          <div className="ct__mfig-progress" role="tablist" aria-label="Figure panels">
            {images.map((_, i) => (
              <div
                key={i}
                role="tab"
                aria-selected={i === panelIdx}
                className={`ct__mfig-seg${i < panelIdx ? ' ct__mfig-seg--done' : ''}`}
              >
                {i === panelIdx && (
                  <div className="ct__mfig-seg-fill" style={{ width: `${progress * 100}%` }} />
                )}
              </div>
            ))}
          </div>

        </div>{/* end ct__mfig-content */}
      </div>{/* end ct__mfig-sticky */}

      {capHL.toolbarPos && (
        <SelectionToolbar
          style={capHL.toolbarStyle}
          onCopy={capHL.copySelection}
          copyDone={capHL.copyDone}
          onPointerDown={capHL.preventDeselect}
          onHighlight={capHL.handleHighlight}
          onRemoveHighlight={capHL.handleRemoveHighlight}
        />
      )}
    </div>
  );
}

function PointerArrow({ x, y }) {
  return (
    <svg
      className="ct__fig-ptr-arrow"
      style={{ left: `${x}%`, top: `${y}%` }}
      width="16" height="28" viewBox="0 0 16 28"
      aria-hidden="true"
    >
      <line x1="8" y1="0" x2="8" y2="17" stroke="white" strokeWidth="6" strokeLinecap="round"/>
      <polygon points="8,28 0,14 16,14" fill="white"/>
      <line x1="8" y1="1" x2="8" y2="17" stroke="var(--blue-primary)" strokeWidth="3" strokeLinecap="round"/>
      <polygon points="8,27 2,15 14,15" fill="var(--blue-primary)"/>
    </svg>
  );
}
